import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { fetchCollectorById } from '../../services/collectorService';

const PageContainer = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  margin-bottom: var(--space-md);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);

  &:hover {
    color: var(--color-accent);
  }
`;

const Hero = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-md);
  overflow: hidden;
  margin-bottom: var(--space-md);
  padding: var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);

  &::after {
    position: absolute;
    right: -54px;
    bottom: -88px;
    width: 190px;
    height: 190px;
    border: 42px solid rgba(130, 172, 216, 0.11);
    border-radius: 50%;
    content: '';
    pointer-events: none;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Avatar = styled.div`
  display: grid;
  width: 92px;
  height: 92px;
  flex: 0 0 92px;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(93, 64, 34, 0.18);
  border-radius: 50%;
  background: var(--color-mist);
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroCopy = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const Kicker = styled.p`
  margin: 0 0 var(--space-xxs);
  color: var(--color-accent);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.02em;
  word-break: break-word;
`;

const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-xs);
  margin-top: var(--space-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const Dot = styled.span`
  color: var(--color-border);
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$tone',
})`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${(props) => {
    if (props.$tone === 'verified') return 'rgba(4, 120, 87, 0.10)';
    if (props.$tone === 'review') return 'rgba(0, 24, 206, 0.08)';
    if (props.$tone === 'attention') return 'rgba(180, 83, 9, 0.10)';
    return 'rgba(93, 64, 34, 0.07)';
  }};
  color: ${(props) => {
    if (props.$tone === 'verified') return 'var(--color-success)';
    if (props.$tone === 'review') return 'var(--color-accent)';
    if (props.$tone === 'attention') return 'var(--color-warning-dark)';
    return 'var(--color-text-muted)';
  }};
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);

  &::before {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    content: '';
  }
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: var(--space-md);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-primary);

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Metric = styled.div`
  padding: var(--space-sm) var(--space-md);
  border-right: 1px solid rgba(255, 255, 255, 0.16);

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 620px) {
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.16);

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const MetricLabel = styled.span`
  display: block;
  color: rgba(255, 255, 255, 0.68);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const MetricValue = styled.strong`
  display: block;
  margin-top: var(--space-xxs);
  color: white;
  font-family: var(--font-serif);
  font-size: 2rem;
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.85fr);
  gap: var(--space-md);
  margin-bottom: var(--space-md);

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  padding: var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
`;

const SectionKicker = styled.span`
  display: block;
  margin-bottom: var(--space-xxs);
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;

const Bio = styled.p`
  margin: var(--space-sm) 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  line-height: 1.75;
  white-space: pre-wrap;
`;

const MutedCopy = styled.p`
  margin: var(--space-sm) 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
`;

const DefinitionList = styled.dl`
  display: grid;
  gap: var(--space-sm);
  margin: var(--space-sm) 0 0;
`;

const Definition = styled.div`
  display: grid;
  grid-template-columns: minmax(104px, 0.75fr) minmax(0, 1.25fr);
  gap: var(--space-sm);
  padding-bottom: var(--space-xs);
  border-bottom: 1px solid var(--color-border-light);

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const Term = styled.dt`
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Description = styled.dd`
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
`;

const ActivitySection = styled.section`
  margin-top: var(--space-md);
`;

const ActivityHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-md);
  margin-bottom: var(--space-sm);
  padding-bottom: var(--space-xs);
  border-bottom: 1px solid var(--color-border);
`;

const ActivityCount = styled.span`
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
`;

const ActivityList = styled.div`
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
`;

const ActivityRow = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(150px, 0.8fr) minmax(120px, 0.6fr);
  gap: var(--space-md);
  align-items: center;
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--color-border-light);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: var(--space-xs);
  }
`;

const ActivityMain = styled.div`
  min-width: 0;
`;

const ActivityTitle = styled.h3`
  margin: 0;
  overflow: hidden;
  color: var(--color-primary);
  font-family: var(--font-sans);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActivitySubtitle = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--color-text-light);
  font-size: var(--font-size-xs);
`;

const ActivityMeta = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const ActivityAmount = styled.strong`
  color: var(--color-primary);
  font-size: var(--font-size-md);
  font-variant-numeric: tabular-nums;
  text-align: right;

  @media (max-width: 680px) {
    text-align: left;
  }
`;

const EmptyActivity = styled.div`
  padding: var(--space-md);
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  text-align: center;
`;

const StatePanel = styled.div`
  max-width: 720px;
  margin: var(--space-xl) auto;
  padding: var(--space-lg);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
  text-align: center;
`;

const StateTitle = styled.h1`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xxl);
`;

const StateCopy = styled.p`
  margin: var(--space-xs) 0 var(--space-md);
  color: var(--color-text-secondary);
`;

const RetryButton = styled.button`
  min-height: 42px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-primary);
  border-radius: var(--border-radius-md);
  background: var(--color-primary);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);

  &:hover {
    border-color: var(--color-accent);
    background: var(--color-accent);
  }
`;

const identityMeta = (collector) => {
  if (collector?.identityVerified || collector?.identityVerificationStatus === 'verified') {
    return {
      label: 'Identity verified',
      tone: 'verified',
      description: 'Stripe Identity verification is complete.',
    };
  }
  if (collector?.identityVerificationStatus === 'processing') {
    return {
      label: 'Verification in review',
      tone: 'review',
      description: 'Identity details have been submitted and are processing.',
    };
  }
  if (collector?.identityVerificationStatus === 'requires_input') {
    return {
      label: 'Verification needs attention',
      tone: 'attention',
      description: 'The collector needs to provide more information.',
    };
  }
  return {
    label: 'Verification not started',
    tone: 'muted',
    description: 'The collector has not submitted identity details yet.',
  };
};

const initialsFor = (name) => {
  const initials = String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  return initials || 'CE';
};

const formatDate = (value, includeTime = false) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

const formatCurrency = (pence, currency = 'GBP') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: String(currency || 'GBP').toUpperCase(),
    maximumFractionDigits: 0,
  }).format((Number(pence) || 0) / 100);

const humanize = (value, fallback = '—') => {
  if (!value) return fallback;
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const CollectorDetail = () => {
  const { collectorId } = useParams();
  const [collector, setCollector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadCollector = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      setCollector(await fetchCollectorById(collectorId));
    } catch (loadError) {
      setError(loadError.message || 'Unable to load collector details');
    } finally {
      setLoading(false);
    }
  }, [collectorId]);

  useEffect(() => {
    loadCollector();
  }, [loadCollector]);

  if (loading) {
    return (
      <PageContainer>
        <StatePanel aria-live="polite">
          <StateTitle>Loading collector…</StateTitle>
          <StateCopy>Gathering profile and marketplace activity.</StateCopy>
        </StatePanel>
      </PageContainer>
    );
  }

  if (error || !collector) {
    return (
      <PageContainer>
        <BackLink to="/collectors">← Back to collectors</BackLink>
        <StatePanel role="alert">
          <StateTitle>Collector unavailable</StateTitle>
          <StateCopy>{error || 'This collector profile could not be found.'}</StateCopy>
          <RetryButton type="button" onClick={loadCollector}>Try again</RetryButton>
        </StatePanel>
      </PageContainer>
    );
  }

  const identity = identityMeta(collector);

  return (
    <PageContainer>
      <BackLink to="/collectors">← Back to collectors</BackLink>

      <Hero>
        <Avatar aria-hidden="true">
          {collector.profileImage ? (
            <AvatarImage src={collector.profileImage} alt="" />
          ) : initialsFor(collector.displayName)}
        </Avatar>
        <HeroCopy>
          <Kicker>Collector profile</Kicker>
          <Title>{collector.displayName}</Title>
          <HeroMeta>
            <span>{collector.email || 'No email available'}</span>
            <Dot aria-hidden="true">•</Dot>
            <span>{collector.country || 'Location not provided'}</span>
            <StatusBadge $tone={identity.tone}>{identity.label}</StatusBadge>
          </HeroMeta>
        </HeroCopy>
      </Hero>

      <Metrics aria-label="Collector marketplace activity">
        <Metric>
          <MetricLabel>Conversations</MetricLabel>
          <MetricValue>{collector.activity.conversations}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Purchases</MetricLabel>
          <MetricValue>{collector.activity.purchases}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Total spent</MetricLabel>
          <MetricValue>{formatCurrency(collector.activity.totalSpentPence)}</MetricValue>
        </Metric>
      </Metrics>

      <DetailGrid>
        <Panel>
          <SectionKicker>Profile</SectionKicker>
          <SectionTitle>About this collector</SectionTitle>
          {collector.bio ? (
            <Bio>{collector.bio}</Bio>
          ) : (
            <MutedCopy>This collector has not added a profile introduction.</MutedCopy>
          )}
        </Panel>

        <Panel>
          <SectionKicker>Account</SectionKicker>
          <SectionTitle>Profile details</SectionTitle>
          <DefinitionList>
            <Definition>
              <Term>Email</Term>
              <Description>{collector.email || 'Not available'}</Description>
            </Definition>
            <Definition>
              <Term>Joined</Term>
              <Description>{formatDate(collector.createdAt)}</Description>
            </Definition>
            <Definition>
              <Term>Last active</Term>
              <Description>{formatDate(collector.activity.lastActiveAt, true)}</Description>
            </Definition>
            <Definition>
              <Term>Identity</Term>
              <Description>
                {identity.label}
                <ActivitySubtitle>{identity.description}</ActivitySubtitle>
              </Description>
            </Definition>
            <Definition>
              <Term>Collector ID</Term>
              <Description>{collector.uid || collector.id}</Description>
            </Definition>
          </DefinitionList>
        </Panel>
      </DetailGrid>

      <ActivitySection>
        <ActivityHeader>
          <div>
            <SectionKicker>Marketplace</SectionKicker>
            <SectionTitle>Recent enquiries</SectionTitle>
          </div>
          <ActivityCount>{collector.recentConversations.length} shown</ActivityCount>
        </ActivityHeader>
        <ActivityList>
          {collector.recentConversations.length === 0 ? (
            <EmptyActivity>No enquiries have been started by this collector.</EmptyActivity>
          ) : collector.recentConversations.map((conversation) => (
            <ActivityRow key={conversation.id}>
              <ActivityMain>
                <ActivityTitle>{conversation.artworkTitle}</ActivityTitle>
                <ActivitySubtitle>With {conversation.artistName}</ActivitySubtitle>
              </ActivityMain>
              <ActivityMeta>{humanize(conversation.status, 'Open')}</ActivityMeta>
              <ActivityAmount>
                {formatDate(
                  conversation.lastMessageAt ||
                  conversation.updatedAt ||
                  conversation.createdAt
                )}
              </ActivityAmount>
            </ActivityRow>
          ))}
        </ActivityList>
      </ActivitySection>

      <ActivitySection>
        <ActivityHeader>
          <div>
            <SectionKicker>Marketplace</SectionKicker>
            <SectionTitle>Recent purchases</SectionTitle>
          </div>
          <ActivityCount>{collector.recentOrders.length} shown</ActivityCount>
        </ActivityHeader>
        <ActivityList>
          {collector.recentOrders.length === 0 ? (
            <EmptyActivity>No purchases have been made by this collector.</EmptyActivity>
          ) : collector.recentOrders.map((order) => (
            <ActivityRow key={order.id}>
              <ActivityMain>
                <ActivityTitle>{order.artworkTitle}</ActivityTitle>
                <ActivitySubtitle>
                  {humanize(order.fulfilmentMethod, 'Fulfilment not set')}
                </ActivitySubtitle>
              </ActivityMain>
              <ActivityMeta>{humanize(order.status)}</ActivityMeta>
              <ActivityAmount>{formatCurrency(order.totalPence, order.currency)}</ActivityAmount>
            </ActivityRow>
          ))}
        </ActivityList>
      </ActivitySection>
    </PageContainer>
  );
};

export default CollectorDetail;
